import type { Ref } from 'react';

import { useTranslation } from 'react-i18next';

import { FormRow, Hint } from '@vestigas/ui';

import { type BugReportForm } from '../utils';

import { CardTextarea } from './CardTextarea';

type P = {
  readonly currentUserEmail: string | null | undefined;
  readonly form: BugReportForm;
  readonly isNameAutoDetected: boolean;
  readonly isNameSystemAccount: boolean;
  readonly isSubmitting: boolean;
  readonly titleInputRef: Ref<HTMLInputElement>;
};

export function BugReportMainFields({
  currentUserEmail,
  form,
  isNameAutoDetected,
  isNameSystemAccount,
  isSubmitting,
  titleInputRef,
}: P) {
  const { t } = useTranslation('bugReport');

  return (
    <div className="flex flex-col gap-8">
      <FormRow>
        <form.AppField name="title">
          {(field) => (
            <field.TextField
              ref={titleInputRef}
              className="flex-1"
              isDisabled={isSubmitting}
              label={t(($) => $.dialog.titleField.label)}
              placeholder={t(($) => $.dialog.titleField.example)}
            />
          )}
        </form.AppField>
      </FormRow>

      <FormRow>
        <form.AppField name="stepsToReproduce">
          {() => (
            <CardTextarea
              disabled={isSubmitting}
              label={t(($) => $.dialog.stepsToReproduce.label)}
              placeholder={t(($) => $.dialog.stepsToReproduce.exampleContent)}
            />
          )}
        </form.AppField>
      </FormRow>

      <FormRow>
        <form.AppField name="expectedBehavior">
          {() => (
            <CardTextarea
              disabled={isSubmitting}
              label={t(($) => $.dialog.expectedBehavior.label)}
              minRows={3}
              placeholder={t(($) => $.dialog.expectedBehavior.example)}
            />
          )}
        </form.AppField>
        <form.AppField name="actualBehavior">
          {() => (
            <CardTextarea
              disabled={isSubmitting}
              label={t(($) => $.dialog.actualBehavior.label)}
              minRows={3}
              placeholder={t(($) => $.dialog.actualBehavior.example)}
            />
          )}
        </form.AppField>
      </FormRow>

      <FormRow>
        <form.AppField name="reporterName">
          {(field) => (
            <field.TextField
              className="flex-1"
              isDisabled={isSubmitting}
              label={t(($) => $.dialog.reporter.label)}
            />
          )}
        </form.AppField>
        <div className="mt-5 flex flex-1 flex-col">
          {isNameAutoDetected && (
            <Hint className="block text-xs">
              {t(($) => $.dialog.reporter.autoDetected)}
            </Hint>
          )}
          {isNameSystemAccount && (
            <Hint className="block text-xs">
              {t(($) => $.dialog.reporter.systemAccount)}
            </Hint>
          )}
          <p className="text-xs text-tiga-text-lighter">{currentUserEmail}</p>
        </div>
      </FormRow>
    </div>
  );
}
